import { Box, Button, Typography } from '@mui/material'
import { format, parse } from 'date-fns'
import * as React from 'react'
import { colors } from '../../constants/colors'
import { GrayBox, HBox, InfoBox, SquareButton, VBox } from '../../elements/basic-elements'
import ArrowRight from '../../images/icons/arrow-right'

import { ScrollCard } from '../../elements/cards'
import GlobalIcon from '../../images/icons/global-icon'

import { ShareButton } from '../share-button'
import { Avatar } from '../user-avatar'
import { LocationBox } from '.'
import { pageState } from '../../context/page-context'
import { blocksToText } from '../../async/sanity/utils'
import { DemocracyEngineDonateButton } from '../democracy-engine-donate-button'

const CandidateCard: React.FC<{
  data: Queries.SanityCandidate
  detail?: boolean
  onDetailClick?: Function
}> = ({ data, detail, onDetailClick }) => {
  const candidateImage = data.image?.asset?.url
  const { isMobile } = pageState()
  const isDesktop = !isMobile

  // console.log("!!!!", data)
  const democracyEngineRecipientKey = data.democracy_engine_candidate && data._id ? data._id : ''
  const donationOrganization =
    democracyEngineRecipientKey && data?.name
      ? ({
          title: data.name
        } as Queries.SanityOrganization)
      : undefined

  return (
    // @ts-expect-error: existing type issue
    <ScrollCard elevation={0}>
      {() => ({
        content: (
          <VBox gap={2} flex={1}>
            <Box
              gap={2}
              sx={{
                flexDirection: isDesktop ? 'row' : 'column',
                alignItems: isDesktop ? 'flex-start' : 'center',
                textAlign: isDesktop ? 'left' : 'center'
              }}
            >
              <HBox justifyContent="space-between" alignItems="center">
                {data.jurisdiction && (
                  <LocationBox type={'state'}>
                    <Typography variant="body2">{data.jurisdiction}</Typography>
                  </LocationBox>
                )}

                <Box>
                  <ShareButton />
                </Box>
              </HBox>
              <HBox mt={2} gap={2}>
                {candidateImage && <Avatar src={candidateImage} size={100} />}

                <VBox flex={1} justifyContent="space-between" alignItems="flex-start">
                  <VBox>
                    <Typography textAlign="left" color="textPrimary" variant="h3">
                      {data.name}
                    </Typography>

                    {data.office && (
                      <Typography variant="body2" textAlign="left" color="textPrimary">
                        Candidate for {data.office ?? ''}
                      </Typography>
                    )}
                  </VBox>
                  {data.election_date && (
                    <Typography textAlign="left" color="info.dark" component="p" fontSize={12}>
                      Election Date:{' '}
                      {format(parse(data.election_date, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')}
                    </Typography>
                  )}
                </VBox>
              </HBox>
            </Box>
            <VBox flex={1} gap={2} justifyContent="start">
              {data.history && data.history.length > 0 && (
                <InfoBox>
                  <Typography color="textPrimary" variant="h6">
                    Political History
                  </Typography>
                  <VBox flexWrap="wrap" gap={0.5}>
                    {data.history.map(({ office, jurisdiction, timespan }, h) => {
                      return (
                        <Typography fontSize={13} color="textPrimary" key={h}>
                          <b>{office}</b>{' '}
                          {timespan ? (
                            <Typography fontSize={13} color={'info.dark'} component="span">
                              {timespan}
                            </Typography>
                          ) : (
                            ''
                          )}
                          {jurisdiction ? (
                            <Typography fontSize={13} color="textPrimary">
                              {jurisdiction}
                            </Typography>
                          ) : (
                            ''
                          )}
                        </Typography>
                      )
                    })}
                  </VBox>
                </InfoBox>
              )}
              {data.endorsements && (
                <Box>
                  <Typography variant="body2" mb={1} color="textPrimary">
                    Endorsed by:
                  </Typography>
                  <HBox gap={1} alignItems="center" flexWrap="wrap">
                    {data.endorsements.map((endorsement, e) => {
                      const logo = endorsement?.organization?.logo?.asset?.url
                      return (
                        <GrayBox key={e}>
                          <HBox gap={1} alignItems="center">
                            {logo && (
                              <img
                                style={{
                                  width: 18,
                                  height: 18,
                                  objectFit: 'cover',
                                  borderRadius: 9,
                                  overflow: 'hidden'
                                }}
                                src={logo}
                              />
                            )}
                            <Typography variant="body2">{endorsement?.name}</Typography>
                          </HBox>
                        </GrayBox>
                      )
                    })}
                  </HBox>
                </Box>
              )}
              {data.issues && (
                <Box>
                  <Typography variant="body2" mb={1} color="textPrimary">
                    Issues supported:
                  </Typography>

                  <HBox gap={1} alignItems="center" flexWrap="wrap">
                    {data.issues?.map((issue, i) => {
                      return (
                        <GrayBox key={i}>
                          <HBox gap={1} alignItems="center">
                            <Typography variant="body2">{issue?.title}</Typography>
                          </HBox>
                        </GrayBox>
                      )
                    })}
                  </HBox>
                </Box>
              )}
              {/* for detail card */}
              <Box>
                {/*@ts-expect-error: existing type issue*/}
                {blocksToText(data.about).map((block, b) => (
                  <Box mb={1.2} key={b}>
                    <Typography variant="body2" color="textPrimary">
                      {block}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </VBox>
          </VBox>
        ),
        footer: (
          <HBox>
            {detail ? (
              <HBox gap={1} width={'100%'}>
                <Button
                  variant="contained"
                  disableElevation
                  color={'primary'}
                  endIcon={<GlobalIcon />}
                  sx={{ flex: 1 }}
                  onClick={() => {
                    data.url && window.open(data.url, '_blank')
                  }}
                >
                  Open Website
                </Button>
              </HBox>
            ) : (
              <HBox gap={1} width={'100%'} justifyContent="flex-end">
                {donationOrganization && (
                  <DemocracyEngineDonateButton
                    organization={donationOrganization}
                    recipientId={democracyEngineRecipientKey}
                    action_id={data.slug?.current || ''}
                    disabled={!democracyEngineRecipientKey}
                    sx={{ flex: 1 }}
                  />
                )}
                {/* <HeartButton /> */}
                <SquareButton
                  variant="contained"
                  color={'secondary'}
                  disableElevation
                  onClick={() => {
                    onDetailClick && onDetailClick()
                  }}
                >
                  <ArrowRight color={colors.WHITE} />
                </SquareButton>
              </HBox>
            )}
          </HBox>
        )
      })}
    </ScrollCard>
  )
}

export default CandidateCard
